/**
 * Builds a consistent tracking key from a flat object.
 *
 * Ignores unset or empty values. Supports primitives and arrays of primitives.
 * Ensures keys and array values are sorted for deterministic output.
 *
 * @param {Record<string, string | number | boolean | null | undefined | Array<string | number | boolean | null | undefined>>} obj
 * @returns {string} Consistent tracking key string
 *
 * @example
  buildTrackingKey({
    aString: 'text',
    aNumber: 42,
    tags: ['b', 'a'],
    ignored: null,
    missing: undefined,
    empty: '',
    falsy: false,
    messy: [1, 'two', false, null]
  });
 * // returns: 'aNumber:42~aString:text~falsy:false~messy:1,false,two~tags:a,b'
 */
export function buildTrackingKey(obj) {
  const PAIR_SEPARATOR = '~';
  const KEY_VALUE_SEPARATOR = ':';
  const ARRAY_SEPARATOR = ',';

  const isMeaningful = (value) => value != null && value !== ''; // skip null, undefined, and empty string

  function formatArrayValue(key, arr) {
    const items = arr.filter(isMeaningful).map(String).sort();

    return items.length
      ? `${key}${KEY_VALUE_SEPARATOR}${items.join(ARRAY_SEPARATOR)}`
      : null;
  }

  return Object.entries(obj)
    .filter(([, value]) => isMeaningful(value))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return formatArrayValue(key, value);
      }

      return `${key}${KEY_VALUE_SEPARATOR}${String(value)}`;
    })
    .filter((item) => item != null)
    .sort((a, b) => a.localeCompare(b))
    .join(PAIR_SEPARATOR);
}

/**
 * Flattens nested objects into a flat object with dot-notation keys.
 * This allows buildTrackingKey to work with complex nested structures.
 *
 * @param {Record<string, any>} obj - Object to flatten
 * @param {string} [prefix=''] - Current key prefix for recursion
 *
 * @example
 * flattenForTrackingKey({
 *   user: { name: 'John', age: 30 },
 *   tags: ['a', 'b'],
 *   active: true
 * });
 * // returns: { 'user.name': 'John', 'user.age': 30, 'tags': ['a', 'b'], 'active': true }
 */
export function flattenForTrackingKey(obj, prefix = '') {
  // Handle undefined/null objects
  if (obj == null) {
    return {};
  }

  /** @type {Record<string, string | number | boolean | null | undefined | Array<string | number | boolean | null | undefined>>} */
  const flattened = {};

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively flatten nested objects
      Object.assign(flattened, flattenForTrackingKey(value, newKey));
    } else {
      // Keep primitives, arrays, null, and undefined as-is
      flattened[newKey] = value;
    }
  }

  return flattened;
}
